import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { FormFields, formMetaData, IndividualField } from '../../shared/dynamic-forms.interface2';
import { FhirResourceTransformService } from '../../shared/fhir-resource-transform.service';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { ErrorService } from '../../shared/error.service';
import { SuccessToastService } from '../../shared/success-toast.service';
import { Location } from 'fhir/r4';

@Component({
  selector: 'app-quick-add-location',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, DynamicFormsV2Component],
  templateUrl: './quick-add-location.component.html',
  styleUrls: ['./quick-add-location.component.scss']
})
export class QuickAddLocationComponent {
  @ViewChild('formRef') formRef!: DynamicFormsV2Component;

  formMetaData: formMetaData;
  formFields: FormFields[];
  childType: string;
  submitting = false;

  constructor(
    public dialogRef: MatDialogRef<QuickAddLocationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { parentId: string, parentType: string },
    private fhirResourceTransformService: FhirResourceTransformService,
    private fhirResourceService: FhirResourceService,
    private errorService: ErrorService,
    private successToast: SuccessToastService
  ) {
    this.childType = (data.parentType || '').toLowerCase() === 'ward' ? 'Room' : 'Bed';
    
    this.formMetaData = {
      formName: `Add New ${this.childType}`,
      formDescription: `Enter details for the new ${this.childType} under this ${data.parentType}.`,
      submitText: `Create ${this.childType}`,
      showSubmitButton: false
    };

    this.formFields = [
      <IndividualField>{
        generalProperties: {
          fieldApiName: 'name',
          fieldName: 'Name',
          fieldLabel: `${this.childType} Name / Number`,
          fieldType: 'IndividualField',
          inputType: 'text',
          validations: [{ type: 'default', name: 'required' }],
          isArray: false,
          isGroup: false
        },
        dataType: 'primitive',
        data: ''
      },
      <IndividualField>{
        generalProperties: {
          fieldApiName: 'description',
          fieldName: 'Description',
          fieldLabel: 'Description (Optional)',
          fieldType: 'IndividualField',
          inputType: 'text',
          validations: [],
          isArray: false,
          isGroup: false
        },
        dataType: 'primitive',
        data: ''
      }
    ];
  }

  onFormChange(event: any) {
    // We can track real-time changes if needed.
  }

  submit() {
    this.submitting = true;
    const formValues = this.formRef.aForm.value;

    if (!formValues.name) {
      this.errorService.openandCloseError('Name is required.');
      this.submitting = false;
      return;
    }

    let newLocation: Location = {
      resourceType: 'Location',
      name: formValues.name,
      description: formValues.description || undefined,
      status: 'active',
      mode: 'instance',
      partOf: {
        reference: `Location/${this.data.parentId}`
      },
      physicalType: {
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/location-physical-type',
          code: this.childType === 'Room' ? 'ro' : 'bd',
          display: this.childType
        }],
        text: this.childType
      }
    };

    newLocation = this.fhirResourceTransformService.transformValues('Location', newLocation) as Location;

    this.fhirResourceService.postResource('Location', newLocation).subscribe({
      next: (res) => {
        this.successToast.success(`Successfully added ${this.childType}!`);
        this.submitting = false;
        this.dialogRef.close(res);
      },
      error: (err) => {
        this.errorService.openandCloseError(`Failed to create ${this.childType}: ` + (err.message || 'Unknown error'));
        this.submitting = false;
      }
    });
  }

  close() {
    this.dialogRef.close();
  }
}
